/**
 * Expandable accordion rows with optional icons (acf.accordion).
 */

"use client";

import { useState } from "react";
import Image from "next/image";
import { TfiAngleDown, TfiAngleUp } from "react-icons/tfi";
import { OpsiHtmlContent } from "../utils/OpsiHtmlContent";
import {
  asArray,
  getMediaUrl,
  hasHtmlContent,
  OPSI_PART_SPACING,
} from "../utils/opsiHelpers";

const OPSI_ACCORDION_ACCENT = "#993189";
const OPSI_ACCORDION_BG = "transparent";
const OPSI_ACCORDION_BODY = "#464646";
const OPSI_ACCORDION_ICON_SIZE = 55;
const OPSI_ACCORDION_ICON_GAP = 16;

const OpsiAccordionToggle = ({ open }) => (
  <span
    className="opsi-accordion__toggle flex h-[36px] w-[36px] flex-none items-center justify-center rounded-full border-2"
    style={{ borderColor: OPSI_ACCORDION_ACCENT }}
    aria-hidden="true"
  >
    {open ? (
      <TfiAngleUp size={14} color={OPSI_ACCORDION_ACCENT} />
    ) : (
      <TfiAngleDown size={14} color={OPSI_ACCORDION_ACCENT} />
    )}
  </span>
);

const OpsiAccordionItem = ({ item, open, onToggle }) => {
  const iconUrl = getMediaUrl(item?.icon);

  if (!hasHtmlContent(item?.header) && !hasHtmlContent(item?.content)) {
    return null;
  }

  const contentIndent =
    OPSI_ACCORDION_ICON_SIZE + OPSI_ACCORDION_ICON_GAP;

  return (
    <div className="opsi-accordion__item !border-b-[1px] !last:border-b-[1px] !first:border-t-[1px]" style={{ borderColor: OPSI_ACCORDION_ACCENT }}>
      <button
        type="button"
        className="opsi-accordion__trigger flex w-full items-center gap-4 py-6 text-left md:gap-5 md:py-7"
        aria-expanded={open}
        onClick={onToggle}
      >
        {iconUrl ? (
          <Image
            src={iconUrl}
            alt={item?.icon?.alt || ""}
            width={item?.icon?.width || OPSI_ACCORDION_ICON_SIZE}
            height={item?.icon?.height || OPSI_ACCORDION_ICON_SIZE}
            className="opsi-accordion__icon h-[55px] w-[55px] flex-none object-contain"
          />
        ) : (
          <span
            className="opsi-accordion__icon-spacer inline-block h-[55px] w-[55px] flex-none"
            aria-hidden="true"
          />
        )}

        <OpsiHtmlContent
          html={item?.header}
          as="span"
          className="opsi-accordion__title min-w-0 flex-1 !text-[1.125rem] !font-bold leading-snug md:!text-[1.25rem] [&_*]:!font-bold"
        />

        <OpsiAccordionToggle open={open} />
      </button>

      {open && hasHtmlContent(item?.content) ? (
        <div
          className="opsi-accordion__panel pb-6 md:pb-7"
          style={{ paddingLeft: `${contentIndent}px` }}
        >
          <OpsiHtmlContent
            html={item.content}
            className="opsi-accordion__content !text-[1rem] leading-relaxed md:!text-[1.0625rem]"
          />
        </div>
      ) : null}
    </div>
  );
};

export const OpsiAccordion = ({
  data,
  className = `opsi-accordion ${OPSI_PART_SPACING}`,
}) => {
  const items = asArray(data);
  const [openIndex, setOpenIndex] = useState(0);

  if (!items.length) {
    return null;
  }

  return (
    <section
      className={`${className} opsi-accordion--styled rounded-none px-0 py-0`}
      data-opsi-part="accordion"
      style={{ backgroundColor: OPSI_ACCORDION_BG }}
    >
      {items.map((item, index) => (
        <OpsiAccordionItem
          key={`${item?.header || "accordion"}-${index}`}
          item={item}
          open={openIndex === index}
          onToggle={() =>
            setOpenIndex((current) => (current === index ? -1 : index))
          }
        />
      ))}

      <style jsx global>{`
        .opsi-accordion--styled .opsi-accordion__title,
        .opsi-accordion--styled .opsi-accordion__title * {
          color: ${OPSI_ACCORDION_ACCENT} !important;
        }

        .opsi-accordion--styled .opsi-accordion__content,
        .opsi-accordion--styled .opsi-accordion__content p,
        .opsi-accordion--styled .opsi-accordion__content li {
          color: ${OPSI_ACCORDION_BODY} !important;
          font-weight: 400 !important;
        }

        .opsi-accordion--styled .opsi-accordion__content ul {
          list-style: disc;
          margin: 0;
          padding-left: 1.25rem;
        }

        .opsi-accordion--styled .opsi-accordion__content ul li {
          margin-bottom: 0.75rem;
        }

        .opsi-accordion--styled .opsi-accordion__content ul li:last-child {
          margin-bottom: 0;
        }

        .opsi-accordion--styled .opsi-accordion__content p {
          margin: 0 0 0.75rem;
        }

        .opsi-accordion--styled .opsi-accordion__content p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </section>
  );
};
